import { ROLE_LABEL } from './domain'
import type { Profile, UserRole } from './types'

/**
 * WHOSE NAME GOES ON THE SCREEN.
 *
 * Management, 01-09-2026: an email address must never appear in a trail, a
 * register or a detail panel. The screen names the person.
 *
 * WHY THIS IS A MODULE AND NOT EIGHT SMALL EDITS
 * ----------------------------------------------
 * Email addresses were on eight screens. Written out eight times, the rule
 * drifts the first time somebody adds a ninth - and this codebase has already
 * been bitten twice by a policy living in two places (the sidebar and the
 * route guard; the board matrix and RESTRICTED_MENUS). So the rule is written
 * once, here, and the screens call it.
 *
 * WHY IT RESOLVES BY EMAIL AND NOT BY id
 * --------------------------------------
 * Some tables record who acted as a uuid (document_audit.actor_id), some as an
 * email (document_approvals.actor_email, inventory_transactions
 * .posted_by_email, grns.creator_email), and a few carry both. A PostgREST
 * embed would need a foreign key on each of them, and several have none. A
 * lookup that accepts EITHER covers all of them with one mechanism and no
 * database change whatsoever.
 *
 * NOTHING IS REWRITTEN
 * --------------------
 * The audit trail keeps the email it always stored - that is the record, and
 * it is what an investigator needs. This changes only what is drawn.
 */

/**
 * How a role reads to a person, when there is no name to show.
 *
 * Imported from domain.ts rather than written out again. The first draft of
 * this file declared its own copy - twelve roles, subtly different wording,
 * "CXO" against domain's "CXO (View Only)" - which is precisely the drift this
 * module exists to prevent, committed inside the module that exists to prevent
 * it. One list.
 */
const roleText = (role: string): string | undefined =>
  (ROLE_LABEL as Record<string, string>)[role]

export interface PeopleIndex {
  byEmail: Record<string, string>
  byId: Record<string, string>
}

/**
 * Build the lookup once from the staff list.
 *
 * INACTIVE PEOPLE ARE INCLUDED, DELIBERATELY. An audit trail is mostly about
 * things that happened in the past, and some of them were done by somebody who
 * has since left. Filtering to active staff would blank the name on exactly
 * the rows an investigator cares about most. useProfiles() already fetches
 * every row, active or not; this relies on that and says so, because a
 * well-meaning `.eq('is_active', true)` added there later would quietly break
 * this and nothing would look wrong.
 */
export function indexPeople(profiles: Profile[]): PeopleIndex {
  const byEmail: Record<string, string> = {}
  const byId: Record<string, string> = {}
  for (const p of profiles) {
    const name = (p.full_name ?? '').trim()
    if (!name) continue
    if (p.email) byEmail[p.email.trim().toLowerCase()] = name
    if (p.id) byId[p.id] = name
  }
  return { byEmail, byId }
}

/**
 * The name to draw, given whatever the row happens to carry.
 *
 * The order is id first, then email: a uuid is a key and an email is a string
 * somebody typed. Two staff records could in principle share a stale email;
 * they cannot share an id.
 *
 * AN EMAIL ADDRESS IS NEVER RETURNED. Not as a fallback, not on hover, not in
 * a title attribute. When there is no name, the role is shown instead - which
 * is what a person reading a trail actually wants to know - and 'System' when
 * there is not even that, because rows written by a trigger have no actor at
 * all and pretending otherwise would be worse than admitting it.
 */
export function personName(
  people: PeopleIndex,
  who: { id?: string | null; email?: string | null; role?: UserRole | string | null },
): string {
  if (who.id && people.byId[who.id]) return people.byId[who.id]
  const email = (who.email ?? '').trim().toLowerCase()
  if (email && people.byEmail[email]) return people.byEmail[email]
  if (who.role) return roleText(String(who.role)) ?? String(who.role)
  return 'System'
}

/** The readable form of a role on its own, for labels and headings. */
export const roleLabel = (role?: UserRole | string | null): string =>
  role ? (roleText(String(role)) ?? String(role)) : '—'
